import React, { memo, useEffect, useRef, useState } from "react";
import "./SmartImage.css";

const SPINNER_SIZE_MAP = {
    sm: "16px",
    md: "28px",
    lg: "44px",
};

const SPINNER_BORDER_MAP = {
    sm: "2px",
    md: "3px",
    lg: "4px",
};

const DEFAULT_SPINNER_DELAY_MS = 120;

function SmartImage({
    src,
    alt = "",
    className,
    spinnerSize = "md",
    width,
    height,
    wrapperClassName,
    wrapperStyle,
    // Pass an explicit fallback when image context differs (campaign vs profile).
    fallbackSrc = null,
    showSpinner = true,
    spinnerDelayMs = DEFAULT_SPINNER_DELAY_MS,
    showSpinnerBackground = false,
    onLoad,
    onError,
    loading,
    decoding,
    ...rest
}) {
    const imageRef = useRef(null);
    const resolvedSrc = src || fallbackSrc || null;
    const [currentSrc, setCurrentSrc] = useState(resolvedSrc);
    const [isLoading, setIsLoading] = useState(Boolean(resolvedSrc));
    const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
    const [hasTriedFallback, setHasTriedFallback] = useState(false);
    const spinnerTimerRef = useRef(null);

    useEffect(() => {
        setCurrentSrc(resolvedSrc);
        setIsLoading(Boolean(resolvedSrc));
        setHasTriedFallback(false);
    }, [resolvedSrc]);

    useEffect(() => {
        if (imageRef.current?.complete) {
            setIsLoading(false);
        }
    }, [currentSrc]);

    useEffect(() => {
        if (spinnerTimerRef.current) {
            clearTimeout(spinnerTimerRef.current);
            spinnerTimerRef.current = null;
        }

        if (!showSpinner || !currentSrc || !isLoading) {
            setIsSpinnerVisible(false);
            return undefined;
        }

        if (spinnerDelayMs <= 0) {
            setIsSpinnerVisible(true);
            return undefined;
        }

        spinnerTimerRef.current = setTimeout(() => {
            setIsSpinnerVisible(true);
            spinnerTimerRef.current = null;
        }, spinnerDelayMs);

        return () => {
            if (spinnerTimerRef.current) {
                clearTimeout(spinnerTimerRef.current);
                spinnerTimerRef.current = null;
            }
        };
    }, [showSpinner, currentSrc, isLoading, spinnerDelayMs]);

    const handleLoad = (event) => {
        setIsLoading(false);
        if (onLoad) {
            onLoad(event);
        }
    };

    const handleError = (event) => {
        if (!hasTriedFallback && fallbackSrc && currentSrc !== fallbackSrc) {
            setCurrentSrc(fallbackSrc);
            setIsLoading(true);
            setHasTriedFallback(true);
            return;
        }

        setIsLoading(false);
        if (onError) {
            onError(event);
        }
    };

    const { style: imageStyle, ...imageRest } = rest;

    const finalWrapperClassName = ["smartImageWrap", wrapperClassName].filter(Boolean).join(" ");
    const finalWrapperStyle = {
        ...(width != null ? { width } : {}),
        ...(height != null ? { height } : {}),
        ...wrapperStyle,
    };
    const resolvedSpinnerSize = spinnerSize ? SPINNER_SIZE_MAP[spinnerSize] || spinnerSize : undefined;
    const resolvedSpinnerBorderWidth = spinnerSize
        ? SPINNER_BORDER_MAP[spinnerSize] || undefined
        : undefined;
    const finalSpinnerStyle = spinnerSize
        ? {
              width: resolvedSpinnerSize,
              height: resolvedSpinnerSize,
              borderWidth: resolvedSpinnerBorderWidth,
          }
        : undefined;
    const resolvedLoading = loading || "lazy";
    const resolvedDecoding = decoding || "async";
    const overlayClassName = [
        "smartImageOverlay",
        showSpinnerBackground ? "smartImageOverlayWithBackground" : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <span className={finalWrapperClassName} style={finalWrapperStyle}>
            {isSpinnerVisible && (
                <span className={overlayClassName}>
                    <span className="smartImageSpinner" style={finalSpinnerStyle} aria-hidden="true" />
                </span>
            )}
            {currentSrc && (
                <img
                    ref={imageRef}
                    src={currentSrc}
                    alt={alt}
                    className={className}
                    loading={resolvedLoading}
                    decoding={resolvedDecoding}
                    style={{
                        ...(width != null ? { width: "100%" } : {}),
                        ...(height != null ? { height: "100%" } : {}),
                        ...imageStyle,
                    }}
                    onLoad={handleLoad}
                    onError={handleError}
                    {...imageRest}
                />
            )}
        </span>
    );
}

export default memo(SmartImage);
